import {
  useFloating,
  autoUpdate,
  offset,
  shift,
  flip,
  useHover,
  useClick,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useDelayGroup,
  FloatingDelayGroup,
  FloatingPortal,
  autoPlacement,
  type Placement,
} from "@floating-ui/react"
import { clsx } from "clsx"
import {
  useState,
  useRef,
  type ReactElement,
  type Ref,
  type ReactNode,
  useCallback,
  useEffect,
} from "react"

import { useMediaQuery } from "@/hooks/useMediaQuery"
import { NO_HOVER_QUERY } from "@/lib/breakpoints"

// Grouped tooltip delay, modeled on Base UI: first hover waits, switching
// between grouped tooltips within TOOLTIP_GROUP_TIMEOUT_MS is instant, close
// is immediate.
const TOOLTIP_GROUP_DELAY = { open: 300, close: 0 }
const TOOLTIP_GROUP_TIMEOUT_MS = 400

export const TooltipProvider = ({ children }: { children: ReactNode }) => (
  <FloatingDelayGroup delay={TOOLTIP_GROUP_DELAY} timeoutMs={TOOLTIP_GROUP_TIMEOUT_MS}>
    {children}
  </FloatingDelayGroup>
)

interface TooltipProps {
  text: ReactNode
  position?: Placement
  children: ReactElement
  relationship?: "description" | "label"
  offset?: number
  disabled?: boolean
  closeOnClick?: boolean
  /** Also open when the nearest focusable ancestor receives keyboard focus. */
  openOnAncestorFocus?: boolean
  /** The trigger only shows this text, so without hover a tap toggles it and
   * a tap elsewhere closes it. Other tooltips stay closed without hover. */
  toggleOnTap?: boolean
}

interface TooltipInteractionProps {
  onClick?: unknown
  onKeyDown?: unknown
  onMouseEnter?: unknown
  onTouchEnd?: unknown
  ref?: Ref<unknown>
}

function setRefValue(ref: Ref<unknown> | undefined, value: unknown) {
  if (typeof ref === "function") {
    ref(value)
    return
  }

  if (ref) {
    ref.current = value
  }
}

export const Tooltip = ({
  text,
  position,
  children,
  relationship = "description",
  offset: offsetValue = 8,
  disabled = false,
  closeOnClick = false,
  openOnAncestorFocus = false,
  toggleOnTap = false,
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<Element | null>(null)
  const openTimeoutRef = useRef<number | null>(null)
  // A trigger's tooltip would cover the menu or dialog it just opened. Disclosure
  // toggles are aria-expanded too, but only popup triggers declare aria-haspopup.
  const { "aria-expanded": ariaExpanded, "aria-haspopup": ariaHasPopup } = children.props as {
    "aria-expanded"?: unknown
    "aria-haspopup"?: unknown
  }
  const popupOpen =
    (ariaExpanded === true || ariaExpanded === "true") &&
    ariaHasPopup !== undefined &&
    ariaHasPopup !== false &&
    ariaHasPopup !== "false"

  useEffect(() => {
    // Close rather than hide, so the tooltip doesn't return when the popup closes.
    // oxlint-disable-next-line react/set-state-in-effect -- close a tooltip opened before its popup
    if (popupOpen) setIsOpen(false)
  }, [popupOpen])

  // Without hover, nothing moves away to close a tooltip that a tap or a
  // focus opened, so it would stay over the screen. Controls carry their
  // own labels, so nothing depends on the tooltip.
  const hoverUnavailable = useMediaQuery(NO_HOVER_QUERY)
  const tapToggles = hoverUnavailable && toggleOnTap
  const suppressed = hoverUnavailable && !toggleOnTap

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open: isOpen && !disabled && !popupOpen && !suppressed,
    onOpenChange: (open) => setIsOpen(open && !popupOpen),
    placement: position,
    middleware: [
      offset(offsetValue),
      ...(position ? [flip()] : [autoPlacement()]),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const { delay } = useDelayGroup(context)
  const hover = useHover(context, {
    move: false,
    delay,
    mouseOnly: true,
  })
  const focus = useFocus(context)
  const click = useClick(context, { enabled: tapToggles })
  const dismiss = useDismiss(context)
  const role = useRole(context, {
    role: "tooltip",
    enabled: relationship === "description",
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ])

  const referenceProps = getReferenceProps() as TooltipInteractionProps
  const originalProps = children.props as TooltipInteractionProps & Record<string, unknown>
  const childProps: Record<string, unknown> = {}

  if (relationship === "label") {
    childProps["aria-label"] = typeof text === "string" ? text : undefined
  } else if (relationship === "description") {
    childProps["aria-describedby"] = context.floatingId
  }

  const handleTouchEnd = (event: React.TouchEvent) => {
    const referenceOnTouchEnd = referenceProps.onTouchEnd
    if (typeof referenceOnTouchEnd === "function") {
      referenceOnTouchEnd(event)
    }

    const originalOnTouchEnd = originalProps.onTouchEnd
    if (typeof originalOnTouchEnd === "function") {
      originalOnTouchEnd(event)
    }

    if (!tapToggles) setTimeout(() => setIsOpen(false), 10)
  }

  const handleMouseOver = (event: React.MouseEvent) => {
    const referenceOnMouseEnter = referenceProps.onMouseEnter
    if (typeof referenceOnMouseEnter === "function") {
      referenceOnMouseEnter(event)
    }

    const originalOnMouseEnter = originalProps.onMouseEnter
    if (typeof originalOnMouseEnter === "function") {
      originalOnMouseEnter(event)
    }

    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const referenceOnKeyDown = referenceProps.onKeyDown
    if (typeof referenceOnKeyDown === "function") {
      referenceOnKeyDown(event)
    }

    const originalOnKeyDown = originalProps.onKeyDown
    if (typeof originalOnKeyDown === "function") {
      originalOnKeyDown(event)
    }
  }

  const handleClick = (event: React.MouseEvent) => {
    const referenceOnClick = referenceProps.onClick
    if (typeof referenceOnClick === "function") {
      referenceOnClick(event)
    }

    const originalOnClick = originalProps.onClick
    if (typeof originalOnClick === "function") {
      originalOnClick(event)
    }

    if (closeOnClick) {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current)
        openTimeoutRef.current = null
      }
      setIsOpen(false)
    }
  }

  const childRef = originalProps.ref
  const setFloating = refs.setFloating
  const setReferenceElementFromNode = useCallback(
    (node: Parameters<typeof refs.setReference>[0]) =>
      setReferenceElement(node instanceof Element ? node : null),
    [],
  )
  const setReference = refs.setReference
  const floatingRef = useCallback(
    (node: Parameters<typeof refs.setFloating>[0]) => setFloating(node),
    [setFloating],
  )
  const ref = useCallback(
    (node: Parameters<typeof refs.setReference>[0]) => {
      setReference(node)
      setReferenceElementFromNode(node)
      setRefValue(childRef, node)
    },
    [childRef, setReference, setReferenceElementFromNode],
  )

  useEffect(() => {
    if (!openOnAncestorFocus || !referenceElement) return

    const focusTarget = referenceElement.parentElement?.closest(
      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    if (!focusTarget) return

    const handleFocus = () => setIsOpen(true)
    const handleBlur = () => setIsOpen(false)
    const handleClick = () => setIsOpen(false)
    focusTarget.addEventListener("focus", handleFocus)
    focusTarget.addEventListener("blur", handleBlur)
    if (closeOnClick) focusTarget.addEventListener("click", handleClick)
    return () => {
      focusTarget.removeEventListener("focus", handleFocus)
      focusTarget.removeEventListener("blur", handleBlur)
      if (closeOnClick) focusTarget.removeEventListener("click", handleClick)
    }
  }, [closeOnClick, openOnAncestorFocus, referenceElement])

  const Child = children.type
  const clonedChild = (
    <Child
      {...originalProps}
      {...referenceProps}
      {...childProps}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={handleMouseOver}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      ref={ref}
    />
  )

  return (
    <>
      {clonedChild}
      {isOpen && !popupOpen && !suppressed && (
        <FloatingPortal>
          <div
            ref={floatingRef}
            id={context.floatingId}
            style={{
              ...floatingStyles,
              visibility: isPositioned ? "visible" : "hidden",
            }}
            className={clsx(
              "z-popover max-w-xs rounded-md bg-neutral-900 px-2 py-1 text-xs text-neutral-50 shadow-sm",
            )}
            {...getFloatingProps()}
          >
            {text}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
